/**
 * Document-type marks for the SharePoint browser.
 *
 * A bid folder is mostly Word, Excel, PowerPoint and PDF, and members scan those lists by SHAPE —
 * the blue W is found before the filename is read. So those four get their own mark in their own
 * product colour, and everything else keeps a neutral page glyph.
 *
 * PDF earns one for the same reason the Office three do, not as an afterthought: the client's own
 * material — the RFP, the annexes, the forms that must be answered — almost always arrives as PDF,
 * so it is the type a member scans a bid folder FOR.
 *
 * Drawn here rather than shipped as brand assets: these are our own glyphs in the products' own
 * colours, which is what a file list needs (recognisable at 16px) without vendoring artwork we do
 * not own into the bundle. They are decorative — the row around them carries the file name, so
 * every mark is `aria-hidden`.
 */

/** The types worth distinguishing. Everything else is a `file`. */
export type FileKind = 'word' | 'excel' | 'powerpoint' | 'pdf' | 'file'

/** Product colours, as Microsoft and Adobe use them on their app tiles. */
const KIND_COLOUR: Record<Exclude<FileKind, 'file'>, string> = {
  word: '#185ABD',
  excel: '#107C41',
  powerpoint: '#C43E1C',
  pdf: '#B30B00',
}

/**
 * What each mark carries, and how big.
 *
 * Three of them are the product's initial — the letter the eye actually matches on. PDF cannot
 * borrow that trick twice: `P` is PowerPoint's, and next to PowerPoint's orange-red the colour
 * alone would not settle it. So it carries the three letters everyone already reads it by, set
 * small enough to fit the page and lifted to stay centred in it at that size.
 */
const KIND_LABEL: Record<Exclude<FileKind, 'file'>, { text: string; size: number; y: number }> = {
  word: { text: 'W', size: 10, y: 17 },
  excel: { text: 'X', size: 10, y: 17 },
  powerpoint: { text: 'P', size: 10, y: 17 },
  pdf: { text: 'PDF', size: 6.5, y: 16.4 },
}

/**
 * Which mark a file name earns. Extension only — SharePoint gives no MIME type on a children
 * listing, and the extension is what the member sees anyway. The macro/template siblings
 * (`.docm`, `.xlsm`, `.pptm`, `.dotx`…) are the same document to a reader, so they share the mark.
 */
export function fileKind(name: string): FileKind {
  const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase()
  if (/^doc[xm]?$/.test(ext) || ext === 'dot' || ext === 'dotx') return 'word'
  if (/^xls[xmb]?$/.test(ext) || ext === 'xlt' || ext === 'xltx') return 'excel'
  if (/^ppt[xm]?$/.test(ext) || ext === 'pps' || ext === 'ppsx' || ext === 'pot') return 'powerpoint'
  if (ext === 'pdf') return 'pdf'
  return 'file'
}

/** The mark for a file name — a document type we distinguish, or the neutral page. */
export function FileIcon({ name }: { name: string }) {
  const kind = fileKind(name)
  if (kind === 'file') {
    return (
      <svg className="spb-ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M14 2.5H6.5A1.5 1.5 0 0 0 5 4v16a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 20V7.5z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path
          d="M14 2.5V7.5h5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  return (
    <svg className="spb-ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {/* One page shape with a folded corner, filled in the product colour… */}
      <path
        d="M14 2.5H6.5A1.5 1.5 0 0 0 5 4v16a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 20V7.5z"
        fill={KIND_COLOUR[kind]}
      />
      <path d="M14 2.5V7.5h5z" fill="#ffffff" fillOpacity="0.45" />
      {/* …and the wordmark, which is what the eye actually matches on. */}
      <text
        x="12"
        y={KIND_LABEL[kind].y}
        textAnchor="middle"
        fontSize={KIND_LABEL[kind].size}
        fontWeight="700"
        fill="#ffffff"
        fontFamily="inherit"
      >
        {KIND_LABEL[kind].text}
      </text>
    </svg>
  )
}
